import { useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlotGroupAdditionalPlotsEnum } from '@/api/rest/resources/types/plotGroup';
import { WidgetType } from '@/api/rest/resources/types/widget';
import { useDebounce } from '@/hooks/useDebounce';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { SKIP_PLOT_GROUP_ID, useAllPlotsForOrganization } from '@/pages/shared/hooks/usePlotsForOrganization';

export const useFormSectionPlots = () => {
  const { t } = useTranslation();

  const organisationId = useOrganizationId();

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 800);

  const searchFilters = useMemo(
    () => (debouncedSearchQuery ? { query: debouncedSearchQuery } : {}),
    [debouncedSearchQuery],
  );
  const deferredSearchFilters = useDeferredValue(searchFilters);

  const allPlotsQuery = useAllPlotsForOrganization(
    organisationId,
    deferredSearchFilters,
    SKIP_PLOT_GROUP_ID,
    undefined,
  );
  const { results: plots } = allPlotsQuery.data;

  return {
    name: 'plot_group_filters_dialog_body_section_plots',
    type: WidgetType.o_form_section,
    visible: true,
    relatedData: {
      previewChildrenLimit: 5,
      spacing: 2,
      className: 'py-4 border-b border-divider',
      label: {
        text: t('global.widget.filters.sections.additional_plot_ids.title'),
        className: 'uppercase typography-overline py-2 font-semibold',
      },
      section: {
        className: 'w-full flex-wrap justify-start items-start',
        centerMain: true,
        direction: 'row',
        spacing: 2,
      },
      formName: PlotGroupAdditionalPlotsEnum.additional_plot_ids,
    },
    widgets: [
      {
        name: 'plot_group_filters_dialog_body_section_plots_search_bar',
        visible: true,
        type: WidgetType.m_search_bar,
        relatedData: {
          searchQuery,
          setSearchQuery,
          placeholder: t('global.widget.filters.sections.additional_plot_ids.search.placeholder'),
          styles: {
            container: 'w-full mb-1',
          },
        },
      },
      {
        name: 'plot_group_filters_dialog_body_section_plots_no_data',
        visible: plots.length === 0,
        type: WidgetType.a_text,
        relatedData: {
          text: t('global.widget.filters.sections.additional_plot_ids.noData'),
          className: 'typography-body2 text-text-secondary pl-2',
        },
      },
      {
        name: 'plot_group_filters_dialog_body_section_plots_form',
        visible: plots.length > 0,
        type: WidgetType.o_form_button,
        relatedData: {
          data: plots.map((plot) => ({
            text: plot.name,
            formName: PlotGroupAdditionalPlotsEnum.additional_plot_ids,
            value: plot.id,
          })),
        },
        repeat: {
          data: 'relatedData.data',
          key: 'value',
          template: {
            name: 'plot_group_filters_dialog_body_section_plots_button',
            type: WidgetType.o_form_button,
            visible: true,
            relatedData: {
              variant: 'outline',
              className: 'w-fit whitespace-nowrap font-normal',
            },
          },
        },
      },
    ],
  };
};
